import React from 'react';
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import Stack from '@mui/material/Stack';
import StarField from '../components/StarField';
import CompassImg from '../artifacts/absolutvision-uCMKx2H1Y38-unsplash.jpg';
import CompSrc from '../artifacts/gripfire-careers-engineering-collaboration.jpg';
import OutboundLink from '../components/OutboundLink';
import { useMediaQuery } from 'react-responsive';

const Contact = () => {
    const shouldStackContent = useMediaQuery({ query: '(max-width: 900px)' });
    const shouldHideImage = useMediaQuery({ query: '(max-width: 900px), (max-height: 500px)' });
    React.useEffect(() => { document.title = 'Gripfire, Inc. - Careers' }, []);

    return (
        <Box sx={rootSx}>
            <StarField />
            <Box sx={topSpacerSx} />
            <Box sx={{ ...wrapperSx, ...(shouldStackContent ? { flexDirection: 'column', alignItems: 'center' } : {}) }}>
                <Box sx={leftColumnSx}>
                    <Typography sx={eyebrowSx}>Build with purpose</Typography>
                    <Typography variant="h2" sx={titleSx}>
                        Do meaningful engineering work.
                    </Typography>

                    <Stack spacing={2}>
                        <Typography variant="h6" sx={descrSx}>
                            Gripfire is looking for thoughtful engineers, problem-solvers, and technical professionals
                            who want to work on practical, mission-focused systems.
                        </Typography>

                        <Typography variant="h6" sx={descrSx}>
                            Gripfire is an Equal Opportunity Employer. All qualified applicants will receive consideration
                            for employment without regard to race, color, religion, sex, national origin, age, disability,
                            or genetic information.
                        </Typography>
                    </Stack>

                    <Box sx={openingsSx}>
                        <OutboundLink
                            eventLabel="current-job-openings"
                            to="https://www.indeed.com/cmp/Gripfire,-Inc./jobs"
                            target="_blank"
                            sx={outboundLinkSx}
                            title="Current Job Openings">
                            View current opportunities
                        </OutboundLink>
                    </Box>
                </Box>
                {!shouldHideImage && (
                    <Box sx={rightColumnSx}>
                        <img src={CompSrc} alt="Engineers collaborating around software architecture" style={imgSx} />
                    </Box>
                )}
            </Box>
            <Box sx={bottomSpacerSx} />
        </Box>
    );
}

export default Contact;

const rootSx = {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 0,
    boxSizing: 'border-box',
    position: 'relative',
    overflowX: 'clip',
    '&::before': {
        opacity: '.1',
        backgroundImage: 'url(' + CompassImg + ')',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        position: 'absolute',
        content: '""',
    }
};

const topSpacerSx = {
    position: 'relative',
    zIndex: 1,
    flex: '0 0 auto',
    height: { xs: 'calc(5.75rem + env(safe-area-inset-top))', sm: '8rem' },
    '@media (max-height: 650px)': {
        height: '4.75rem'
    },
    '@media (orientation: landscape) and (max-height: 500px)': {
        height: '4.5rem'
    }
};

const bottomSpacerSx = {
    position: 'relative',
    zIndex: 1,
    flex: '0 0 auto',
    height: { xs: 'calc(8rem + env(safe-area-inset-bottom))', sm: '6rem' },
    '@media (max-height: 650px)': {
        height: '8rem'
    },
    '@media (orientation: landscape) and (max-height: 500px)': {
        height: '10rem'
    }
};

const openingsSx = {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    mt: 2
}

const wrapperSx = {
    position: 'relative',
    zIndex: 1,
    width: 'min(1120px, calc(100% - 1.5rem))',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: { xs: '2rem', sm: '3rem' }
};

const outboundLinkSx = {
    color: 'white',
    textDecoration: 'none',
    fontSize: 'large',
    '&:hover': { textDecoration: 'underline' }
};

const eyebrowSx = {
    mb: 2,
    color: '#ff9b66',
    fontSize: '.78rem',
    fontWeight: 800,
    lineHeight: 1.4,
    letterSpacing: { xs: '.1em', sm: '.18em' },
    textTransform: 'uppercase'
};

const titleSx = {
    pb: 2,
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontWeight: 800,
    fontSize: { xs: '2.25rem', sm: '3.75rem' },
    letterSpacing: { xs: '-.03em', sm: '-.045em' },
    lineHeight: 1.04,
    '@media (orientation: landscape) and (max-height: 500px)': {
        fontSize: '2rem'
    }
}

const descrSx = {
    fontFamily: 'Roboto Slab',
    '@media (orientation: landscape) and (max-height: 500px)': {
        fontSize: '1rem',
        lineHeight: 1.55
    }
};

const leftColumnSx = {
    p: 0,
    pb: { xs: 'calc(5rem + env(safe-area-inset-bottom))', sm: '4rem' },
    maxWidth: '520px',
    '@media (max-height: 650px)': {
        pb: '8rem'
    },
    '@media (orientation: landscape) and (max-height: 500px)': {
        pb: '10rem'
    }
}

const rightColumnSx = {
    width: '520px',
    maxWidth: '520px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start'
}

const imgSx = {
    width: 'min(420px, 100%)',
    aspectRatio: '4 / 3',
    objectFit: 'cover' as const,
    borderRadius: '22px',
    border: '1px solid rgba(255,255,255,.12)',
    boxShadow: '0 28px 80px rgba(0,0,0,.36)'
}